import type { ChangeEvent } from "react";

import { AlertV4, ButtonV4, ChipV4, FieldV4, SelectV4 } from "../common/ui";
import type { AdrWarning, PackageEntitlement } from "../common/sequence/OrderSheetV4";

import {
  CATEGORY_LABELS,
  CATEGORY_TONES,
  SEARCH_MAX_RESULTS,
  SEARCH_MIN_QUERY,
  SHEET_CATEGORIES,
  formatPickPrice,
  isCapsuleDrug,
  type PickItem,
  type SheetCategory,
} from "./ordersModelV4";
import { DrugIcon, StarButton } from "./OrderLineIconsV4";

// Types
type Panel = "sets" | "favs";

type OrderSheetSearchV4Props = {
  // data
  query: string;
  category: SheetCategory | "all";
  results: PickItem[];
  isSearching: boolean;
  error?: unknown;
  panel: Panel | "";
  setCount: number;
  favCount: number;
  favKeys: Set<string>;
  adrOf: (pick: PickItem) => AdrWarning | undefined;
  packageOf: (pick: PickItem) => PackageEntitlement | undefined;
  // callback
  onChangeQuery: (value: string) => void;
  onChangeCategory: (value: SheetCategory | "all") => void;
  onTogglePanel: (panel: Panel) => void;
  onAdd: (pick: PickItem) => void;
  onToggleFavourite: (pick: PickItem) => void;
};

// Const
const CATEGORY_OPTIONS = [
  { value: "all", text: "ทุกหมวด" },
  ...SHEET_CATEGORIES.map((category) => ({ value: category, text: CATEGORY_LABELS[category] })),
];

/** ค้นหารายการสั่งรายตัว + ปุ่มเปิด Order Set / รายการโปรด */
const OrderSheetSearchV4 = (props: OrderSheetSearchV4Props) => {
  const { results } = props;

  const queryLength = props.query.trim().length;

  const hasQuery = queryLength >= SEARCH_MIN_QUERY;

  // Handler
  const handleChangeQuery = (e: ChangeEvent<HTMLInputElement>) => {
    props.onChangeQuery(e.target.value);
  };

  const handleChangeCategory = (value: string) => {
    props.onChangeCategory(value as SheetCategory | "all");
  };

  // Render
  const renderResult = (pick: PickItem) => {
    const adr = props.adrOf(pick);
    const pkg = props.packageOf(pick);

    return (
      <div key={pick.key} className="hv4-ord-result">
        <button type="button" className="hv4-ord-result-btn" onClick={() => props.onAdd(pick)}>
          <ChipV4 size="sm" tone={CATEGORY_TONES[pick.category]}>
            {CATEGORY_LABELS[pick.category]}
          </ChipV4>
          {pick.category === "drug" && (
            <DrugIcon isCapsule={isCapsuleDrug({ stock_unit_name: pick.item?.raw.unit_name })} />
          )}
          <b className="hv4-ord-result-name">{pick.name}</b>
          {!!adr && (
            <span className="hv4-ord-result-adr">
              เตือน: แพ้ {adr.adversary}
              {adr.locked ? " · ห้ามสั่ง" : ""}
            </span>
          )}
          {!!pkg && (
            <span className="hv4-ord-result-pkg">
              📦 {pkg.packageName} · คงเหลือ {pkg.left}
            </span>
          )}
          <span className="hv4-ord-result-price">{formatPickPrice(pick)}</span>
        </button>
        <StarButton
          name={pick.name}
          isOn={props.favKeys.has(pick.key)}
          onClick={() => props.onToggleFavourite(pick)}
        />
      </div>
    );
  };

  const renderResults = () => (
    <div className="hv4-ord-results">
      {results.slice(0, SEARCH_MAX_RESULTS).map(renderResult)}
      {!!props.error && !props.isSearching && (
        <AlertV4 tone="danger" error={props.error}>
          ค้นหาไม่สำเร็จ:{" "}
        </AlertV4>
      )}
      {results.length === 0 && !props.error && !props.isSearching && (
        <div className="hv4-ord-results-empty">ไม่พบรายการ ลองเปลี่ยนคำค้นหรือหมวด</div>
      )}
      {results.length > SEARCH_MAX_RESULTS && (
        <div className="hv4-ord-results-more">
          แสดง {SEARCH_MAX_RESULTS} จาก {results.length} รายการ · พิมพ์ชื่อเพิ่มเพื่อกรองให้แคบลง
        </div>
      )}
    </div>
  );

  return (
    <section className="hv4-card hv4-ord-search">
      <div className="hv4-ord-search-row">
        <FieldV4 className="hv4-ord-search-query" label="ค้นหารายการสั่งรายตัว" htmlFor="hv4_ord_q">
          <input
            id="hv4_ord_q"
            className="hv4-input"
            placeholder="พิมพ์ชื่อแลป ยา หัตถการ ฯลฯ"
            value={props.query}
            onChange={handleChangeQuery}
          />
        </FieldV4>
        <FieldV4 className="hv4-ord-search-cat" label="หมวด" htmlFor="hv4_ord_c">
          <SelectV4
            id="hv4_ord_c"
            options={CATEGORY_OPTIONS}
            value={props.category}
            onChange={handleChangeCategory}
          />
        </FieldV4>
        <ButtonV4
          variant={props.panel === "sets" ? "primary" : "ghost"}
          aria-expanded={props.panel === "sets"}
          onClick={() => props.onTogglePanel("sets")}
        >
          Order Set <b>{props.setCount}</b>
        </ButtonV4>
        <ButtonV4
          variant={props.panel === "favs" ? "primary" : "ghost"}
          aria-expanded={props.panel === "favs"}
          onClick={() => props.onTogglePanel("favs")}
        >
          ★ รายการโปรด <b>{props.favCount}</b>
        </ButtonV4>
      </div>

      {queryLength > 0 && !hasQuery && (
        <div className="hv4-ord-results">
          <div className="hv4-ord-results-more">
            พิมพ์อย่างน้อย {SEARCH_MIN_QUERY} ตัวอักษรเพื่อค้นหา
          </div>
        </div>
      )}

      {hasQuery && renderResults()}
    </section>
  );
};

OrderSheetSearchV4.displayName = "OrderSheetSearchV4";

export default OrderSheetSearchV4;
